import { ArrowLeft, Check, Circle, Eye, EyeOff } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useId, useState } from 'react';
import { signIn as googleSignIn } from '../auth/firebase';
import {
  askPasswordCode,
  clearSessionEnded,
  type CodeSent,
  passwordLogin,
  resendCode,
  SignInError,
  setPassword,
  submitCode,
} from '../auth/session';
import { checklist } from '../lib/passwordRules';

/**
 * Firebase's sign-in codes, turned into the console setting they actually name.
 *
 * Every one of these presents identically to whoever clicked: a popup that opens
 * and closes. The code is the only thing that distinguishes them, so it is shown
 * verbatim alongside the sentence -- an operator pasting `auth/unauthorized-domain`
 * into a search finds the right page in one hop, where "sign-in failed" does not.
 */
function describeGoogleError(error: unknown): string {
  const code = (error as { code?: string } | null)?.code || '';
  const known: Record<string, string> = {
    'auth/operation-not-allowed':
      'Google sign-in is not enabled on this Firebase project. Firebase console -> Authentication -> Sign-in method -> enable Google.',
    'auth/unauthorized-domain': `This hostname (${window.location.hostname}) is not on the project's authorized domains. Firebase console -> Authentication -> Settings -> Authorized domains -> add it.`,
    'auth/api-key-not-valid': 'The Firebase web API key in console/src/auth/firebase.ts is wrong for this project.',
    'auth/configuration-not-found':
      'Firebase Authentication has not been set up on this project yet. Open Authentication in the Firebase console and get started.',
    // Genuinely the person's own doing, and the only entry here that is not a
    // misconfiguration -- worth separating so it does not send anyone to a
    // settings page that is already correct.
    'auth/popup-closed-by-user': 'The sign-in window was closed before it finished.',
    'auth/cancelled-popup-request': 'Another sign-in window was already open.',
    'auth/popup-blocked': 'The browser blocked the sign-in popup. Allow popups for this site and retry.',
    'auth/account-exists-with-different-credential':
      'This address already has a non-Google sign-in on this Firebase project, so Google cannot be attached to it here. Remove that account in Firebase console -> Authentication -> Users, then sign in with Google.',
  };
  const explanation =
    known[code] || (error instanceof Error ? error.message : 'Sign-in failed for an unknown reason.');
  return code ? `${explanation} (${code})` : explanation;
}

/**
 * Where the person is in signing in.
 *
 *   start   Google, or an address and a password
 *   code    the password was right; the code mailed to the address
 *   ask     setting or resetting a password: which address
 *   choose  the code mailed for that, and the new password
 */
type Step =
  | { kind: 'start' }
  | { kind: 'code'; sent: CodeSent }
  | { kind: 'ask' }
  | { kind: 'choose'; sent: CodeSent };

const LAST_EMAIL = 'watch.lastEmail';
const lastEmail = () => {
  try {
    return window.localStorage.getItem(LAST_EMAIL) ?? '';
  } catch {
    return '';
  }
};
const keepEmail = (email: string) => {
  try {
    window.localStorage.setItem(LAST_EMAIL, email.trim().toLowerCase());
  } catch {
    // Not remembered; it is only a convenience.
  }
};

const message = (error: unknown) =>
  error instanceof SignInError
    ? error.weaknesses.length
      ? 'That password is not strong enough — see what is missing below.'
      : error.message
    : error instanceof Error
      ? error.message
      : String(error);

const minutesLeft = (expiresAt: string) =>
  Math.max(1, Math.round((new Date(expiresAt).getTime() - Date.now()) / 60_000));

/**
 * The sign-in screen: Google, or a sphere-watch password with a code by email.
 *
 * A password is set -- the first time, or after it is forgotten -- by a code
 * mailed to the address, from here, with nobody else involved. Whether an
 * address can use sphere-watch is never said before its password or code was
 * right: asking for a code answers the same for every address.
 *
 * `notice` is why the last password sign-in ended, when the server ended it.
 */
export function SignIn({ notice }: { notice: string | null }) {
  const [step, setStep] = useState<Step>({ kind: 'start' });
  const [email, setEmail] = useState(lastEmail);
  const [password, setPasswordValue] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [fresh, setFresh] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(notice);
  const [weak, setWeak] = useState<string[]>([]);
  const newPasswordId = useId();
  // Taken into `info` above; cleared so a later visit here -- after a Google
  // sign-out, say -- does not repeat it.
  useEffect(() => clearSessionEnded(), []);

  const go = (next: Step) => {
    setStep(next);
    setError(null);
    setInfo(null);
    setCode('');
    setWeak([]);
  };

  /** One request at a time, its refusal shown, its success left to `work`. */
  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await work();
    } catch (caught) {
      setError(message(caught));
      if (caught instanceof SignInError) setWeak(caught.weaknesses.map((rule) => rule.key));
      // A code that has had its tries, or its minutes, is only fixed by a new one.
      if (caught instanceof SignInError && caught.code === 'code_expired') setCode('');
    } finally {
      setBusy(false);
    }
  };

  const google = () =>
    run(async () => {
      // NOT `void signIn()`. Every way this fails is a rejected promise carrying
      // a Firebase code that names the exact console setting to change --
      // auth/operation-not-allowed is the Google provider being off,
      // auth/unauthorized-domain is this hostname missing from the Authorized
      // domains list. Discarding it leaves an operator with a popup that
      // flashes and closes and nothing at all to act on.
      try {
        await googleSignIn();
      } catch (caught) {
        throw new Error(describeGoogleError(caught));
      }
    });

  const login = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      keepEmail(email);
      const sent = await passwordLogin(email, password);
      // Null is signed in: this browser was remembered for the address.
      if (sent) {
        setPasswordValue('');
        go({ kind: 'code', sent });
      }
    });
  };

  const verify = (event: FormEvent, sent: CodeSent) => {
    event.preventDefault();
    void run(() => submitCode(sent.challenge, code, remember));
  };

  const resend = (sent: CodeSent) =>
    run(async () => {
      const { expiresAt } = await resendCode(sent.challenge);
      setStep((current) => (current.kind === 'start' || current.kind === 'ask' ? current : { ...current, sent: { ...sent, expiresAt } }));
      setCode('');
      setInfo(`A new code is on its way to ${sent.sentTo}. The last one no longer works.`);
    });

  const ask = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      keepEmail(email);
      const sent = await askPasswordCode(email);
      setFresh('');
      setConfirm('');
      go({ kind: 'choose', sent });
    });
  };

  const choose = (event: FormEvent, sent: CodeSent) => {
    event.preventDefault();
    void run(() => setPassword({ email, challenge: sent.challenge, code, password: fresh, remember }));
  };

  const codeField = (
    <label className="field">
      <span className="field-label">Code</span>
      <input
        className="input wt-signin-code"
        name="one-time-code"
        autoComplete="one-time-code"
        inputMode="numeric"
        pattern="[0-9 ]*"
        maxLength={7}
        placeholder="123456"
        required
        // biome-ignore lint/a11y/noAutofocus: the field appears because a code was just sent; typing it is the only thing left to do
        autoFocus
        value={code}
        onChange={(event) => setCode(event.target.value)}
      />
    </label>
  );

  const rememberBox = (days: number) => (
    <label className="wt-perm wt-signin-remember">
      <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} />
      <span>
        <span className="wt-perm-label">Remember this browser for {days} days</span>
        <span className="wt-perm-help">
          It will still ask for your password here, but not for a code. Leave this off on a shared computer.
        </span>
      </span>
    </label>
  );

  const back = (
    <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => go({ kind: 'start' })}>
      <ArrowLeft size={14} /> Back
    </button>
  );

  const notes = (
    <>
      {info && (
        <p className="wt-signin-info" role="status">
          {info}
        </p>
      )}
      {error && (
        <p className="wt-signin-error" role="alert">
          {error}
        </p>
      )}
    </>
  );

  let body: ReactNode;
  if (step.kind === 'start') {
    body = (
      <>
        <button type="button" className="btn btn-primary wt-signin-wide" disabled={busy} onClick={() => void google()}>
          Sign in with Google
        </button>
        <div className="wt-signin-or">
          <span>or with your sphere-watch password</span>
        </div>
        <form className="wt-signin-form" onSubmit={login}>
          <label className="field">
            <span className="field-label">Email</span>
            <input
              className="input"
              type="email"
              name="username"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Password</span>
            <input
              className="input"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPasswordValue(event.target.value)}
            />
          </label>
          <button type="submit" className="btn btn-secondary wt-signin-wide" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <button type="button" className="wt-signin-link" disabled={busy} onClick={() => go({ kind: 'ask' })}>
            Set or reset your password
          </button>
        </form>
        {notes}
        <p className="wt-signin-foot">
          Use your LimoSphere Google account, or the password you set for sphere-watch — a password sign-in also
          asks for a code sent to your email. What you can open is set by an admin on the Users screen.
        </p>
      </>
    );
  } else if (step.kind === 'code') {
    const { sent } = step;
    body = (
      <form className="wt-signin-form" onSubmit={(event) => verify(event, sent)}>
        <h2 className="wt-signin-step">Check your email</h2>
        <p className="wt-signin-lead">
          We sent a 6-digit code to <strong>{sent.sentTo}</strong>. It works once, for {minutesLeft(sent.expiresAt)}{' '}
          minutes.
        </p>
        {codeField}
        {rememberBox(sent.rememberDays)}
        <button type="submit" className="btn btn-primary wt-signin-wide" disabled={busy}>
          {busy ? 'Checking…' : 'Verify and sign in'}
        </button>
        <div className="wt-signin-row">
          {back}
          <button type="button" className="wt-signin-link" disabled={busy} onClick={() => void resend(sent)}>
            Send a new code
          </button>
        </div>
        {notes}
      </form>
    );
  } else if (step.kind === 'ask') {
    body = (
      <form className="wt-signin-form" onSubmit={ask}>
        <h2 className="wt-signin-step">Set or reset your password</h2>
        <p className="wt-signin-lead">
          Enter your address. If it can use sphere-watch, we will email it a code to choose a password with.
        </p>
        <label className="field">
          <span className="field-label">Email</span>
          <input
            className="input"
            type="email"
            name="username"
            autoComplete="username"
            required
            // biome-ignore lint/a11y/noAutofocus: the step appears because "Set or reset" was just pressed; focus belongs in it
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <button type="submit" className="btn btn-primary wt-signin-wide" disabled={busy}>
          {busy ? 'Sending…' : 'Email me a code'}
        </button>
        <div className="wt-signin-row">{back}</div>
        {notes}
      </form>
    );
  } else {
    const { sent } = step;
    const rules = checklist(fresh, email);
    const strong = rules.every((rule) => rule.met);
    const matches = fresh === confirm;
    body = (
      <form className="wt-signin-form" onSubmit={(event) => choose(event, sent)}>
        <h2 className="wt-signin-step">Choose a password</h2>
        <p className="wt-signin-lead">
          If <strong>{sent.sentTo}</strong> can use sphere-watch, a code is on its way to it. It works once, for{' '}
          {minutesLeft(sent.expiresAt)} minutes.
        </p>
        {codeField}
        {/* A div, not a label: the Show button sits beside the field name, and a
            <label> wrapping a button labels the button instead of the input. */}
        <div className="field">
          <span className="wt-signin-label-row">
            <label className="field-label" htmlFor={newPasswordId}>
              New password
            </label>
            <button
              type="button"
              className="wt-signin-link"
              onClick={() => setReveal(!reveal)}
              aria-label={reveal ? 'Hide the password' : 'Show the password'}
            >
              {reveal ? <EyeOff size={13} /> : <Eye size={13} />} {reveal ? 'Hide' : 'Show'}
            </button>
          </span>
          <input
            id={newPasswordId}
            className="input"
            type={reveal ? 'text' : 'password'}
            name="new-password"
            autoComplete="new-password"
            required
            value={fresh}
            aria-invalid={weak.length > 0 && !strong}
            onChange={(event) => setFresh(event.target.value)}
          />
        </div>
        <ul className="wt-rules" aria-label="What the password needs">
          {rules
            .filter((rule) => rule.key !== 'max' || !rule.met)
            .map((rule) => (
              <li key={rule.key} className={rule.met ? 'is-met' : weak.includes(rule.key) ? 'is-refused' : undefined}>
                {rule.met ? <Check size={13} /> : <Circle size={11} />}
                {rule.label}
              </li>
            ))}
        </ul>
        <label className="field">
          <span className="field-label">Confirm it</span>
          <input
            className="input"
            type={reveal ? 'text' : 'password'}
            name="confirm-password"
            autoComplete="new-password"
            required
            value={confirm}
            aria-invalid={Boolean(confirm) && !matches}
            onChange={(event) => setConfirm(event.target.value)}
          />
          {confirm && !matches && <span className="field-error">The two passwords are not the same.</span>}
        </label>
        {rememberBox(sent.rememberDays)}
        <button type="submit" className="btn btn-primary wt-signin-wide" disabled={busy || !strong || !matches}>
          {busy ? 'Saving…' : 'Set password and sign in'}
        </button>
        <div className="wt-signin-row">
          {back}
          <button type="button" className="wt-signin-link" disabled={busy} onClick={() => void resend(sent)}>
            Send a new code
          </button>
        </div>
        {notes}
        <p className="wt-signin-foot">
          Setting a password signs you out of every other password sign-in and forgets every remembered browser.
        </p>
      </form>
    );
  }

  return (
    <div className="wt-signin">
      <div className="card">
        <h1 style={{ fontSize: 18, marginTop: 0 }}>sphere-watch</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '6px 0 20px' }}>Internal monitoring for sphere-crm and dispatch-be.</p>
        {body}
      </div>
    </div>
  );
}
